import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { MEDIA } from "../constants/media";
import { CheckCircle2, Clock, TrendingUp, Users, Sparkles, Zap, ArrowRight, Quote } from "lucide-react";

const STORIES = [
  {
    id: "ana",
    persona: "Candidata · Restauração",
    name: "Ana Silva",
    role: "Empregada de mesa",
    image: MEDIA.categoryRestauracao,
    headline: "Da candidatura à entrevista em 3 dias, no restaurante do Chiado.",
    challenge:
      "A Ana já tinha 4 anos em sala mas as candidaturas em portais generalistas ficavam sem resposta. Precisava de destaque, uma forma de mostrar experiência real e feedback rápido sobre o CV.",
    steps: [
      { icon: Sparkles, title: "Perfil em 5 minutos", detail: "Criou conta, importou o CV em PDF e a IA extraiu 8 competências (serviço à mesa, HACCP, TPA, inglês B2)." },
      { icon: TrendingUp, title: "Filtro por distância", detail: "Definiu Lisboa-concelho e raio 25 km. Viu 18 vagas ordenadas por proximidade, com \"a X km\" em cada cartão." },
      { icon: Zap, title: "Candidatura com match", detail: "Antes de se candidatar viu o match score 87/100 e o resumo automático — sabia à partida que era um bom encaixe." },
      { icon: Clock, title: "Resposta em 48h", detail: "A empresa passou-a a \"Em análise\" no dia seguinte e a \"Entrevista\" ao terceiro dia. Tudo acompanhado no painel dela." },
    ],
    result: [
      "Entrevista marcada em 3 dias.",
      "IA ilimitada ganha por convidar 3 amigas (referral).",
      "Perfil premium ativo por 30 dias.",
    ],
    quote:
      "Enviei o CV em PDF, a IA disse-me o que faltava, corrigi e candidatei-me com match 87. Fui a entrevista na semana seguinte.",
  },
  {
    id: "diogo",
    persona: "Candidato · Logística",
    name: "Diogo Ferreira",
    role: "Motorista C+E · CAM válido",
    image: MEDIA.categoryLogistica,
    headline: "Trocou de operador em 5 dias — com diária TIR premium.",
    challenge:
      "Motorista há 6 anos, o Diogo procurava mudar para um operador com escalas mais previsíveis. Não tinha CV atualizado — só um telemóvel Android.",
    steps: [
      { icon: Sparkles, title: "Perfil mobile-first", detail: "Registou-se pelo telemóvel em 3 minutos. Sem CV, indicou headline + distrito no formulário do perfil." },
      { icon: TrendingUp, title: "Alerta por categoria", detail: "Criou um alerta \"Logística + Porto\" — recebeu novas vagas assim que apareceram no mural." },
      { icon: Zap, title: "Match 93 com LogisNorte", detail: "A vaga estava em DESTAQUE no topo. Match 93/100 (Carta C, CAM, sem inibições) — resumo em uma frase pela IA." },
      { icon: Clock, title: "Aceite ao 5º dia", detail: "Após 1 dia em análise, entrevista telefónica e no 5º dia a candidatura passou a \"Aceite\" com nota da empresa." },
    ],
    result: [
      "Aceite em 5 dias com diária TIR premium.",
      "Sem CV inicial — perfil suficiente para 4 candidaturas.",
      "Novas vagas por alerta automático.",
    ],
    quote:
      "Fiz tudo pelo telemóvel. Sem CV, com o perfil bem preenchido a IA calculou match 93 e a empresa contactou-me no dia a seguir.",
  },
  {
    id: "sabores",
    persona: "Empresa · Restauração",
    name: "Grupo Sabores do Tejo",
    role: "6 restaurantes · Lisboa e Margem Sul",
    image: MEDIA.categoryHotelaria,
    headline: "18 candidatos qualificados em 5 dias, com DESTAQUE MAX.",
    challenge:
      "Abertura de nova unidade em Belém a exigir cozinheiro/a de 2ª e empregado/a de mesa. Duas semanas para preencher, tempo escasso para triar candidatos manualmente.",
    steps: [
      { icon: Sparkles, title: "Wizard com IA em 3 passos", detail: "Escreveram \"empregado mesa Chiado sala 80 lugares turnos rotativos\" e a IA gerou título, descrição estruturada e requisitos." },
      { icon: Zap, title: "DESTAQUE MAX 99€", detail: "Ativaram o DESTAQUE MAX (30 dias, prioridade 1). Em 24h a vaga tinha 340 visualizações e 12 candidatos." },
      { icon: Users, title: "Triagem em massa com IA", detail: "Um clique em \"Screen\" e a IA calculou match score para 5 candidaturas — poupou 30 minutos de leitura de CVs." },
      { icon: TrendingUp, title: "Employer Branding", detail: "Perfil público (/empresas/grupo-sabores-do-tejo) com cultura, benefícios (formação paga, folga aniversário) e 5 reviews média 4,4." },
    ],
    result: [
      "18 candidaturas em 5 dias (3× a média).",
      "12 com match ≥ 70/100.",
      "Reviews médio 4,4/5 nos últimos 90 dias.",
    ],
    quote:
      "Publicar demorou 8 minutos. A IA fez a triagem, o boost trouxe visibilidade e o perfil público converteu — contratámos os dois no fim da primeira semana.",
  },
];

function Story({ story, index }) {
  const isEven = index % 2 === 0;
  return (
    <article data-testid={`case-${story.id}`} className="max-w-6xl mx-auto px-4 sm:px-8 py-16 border-t border-black/5 first:border-t-0">
      <div className={`grid lg:grid-cols-12 gap-10 items-start ${isEven ? "" : "lg:[direction:rtl]"}`}>
        <div className="lg:col-span-5 lg:[direction:ltr]">
          <div className="sticky top-24">
            <div className="rounded-2xl overflow-hidden border border-black/5 shadow-sm">
              <img src={story.image} alt={`${story.name} — ${story.role}`}
                className="w-full h-64 object-cover" loading="lazy" />
            </div>
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-amber-600 mt-6">{story.persona}</p>
            <h2 className="font-heading font-black tracking-tighter text-2xl sm:text-3xl text-slate-900 mt-2 leading-tight">
              {story.name}
            </h2>
            <p className="text-sm text-slate-600 mt-1">{story.role}</p>

            <blockquote className="mt-6 rounded-2xl bg-slate-950 text-white p-5 relative">
              <Quote className="w-5 h-5 text-amber-400 absolute top-4 right-4" aria-hidden="true" />
              <p className="text-sm leading-relaxed italic">“{story.quote}”</p>
              <p className="text-xs text-slate-400 mt-3">— {story.name}</p>
            </blockquote>
          </div>
        </div>

        <div className="lg:col-span-7 lg:[direction:ltr]">
          <h3 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-slate-900 leading-tight">
            {story.headline}
          </h3>
          <p className="text-slate-600 mt-4 leading-relaxed">{story.challenge}</p>

          <h4 className="font-heading font-bold tracking-tight text-sm uppercase tracking-[0.18em] text-slate-500 mt-8">Passo a passo</h4>
          <ol className="mt-4 space-y-3">
            {story.steps.map((s, i) => (
              <li key={s.title} data-testid={`case-${story.id}-step-${i + 1}`}
                className="flex items-start gap-4 rounded-2xl border border-black/5 bg-white p-5">
                <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
                  <s.icon className="w-5 h-5 text-amber-400" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="font-heading font-bold tracking-tighter text-slate-900">
                    <span className="text-amber-600 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </p>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.detail}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700 mb-3">Resultado</p>
            <ul className="space-y-2">
              {story.result.map((r) => (
                <li key={r} className="flex items-start gap-2 text-sm text-emerald-900">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function CasesStudy() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      <section className="max-w-5xl mx-auto px-4 sm:px-8 pt-14 sm:pt-20 pb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 mb-3">Casos de estudo</p>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-slate-900 leading-[1.05]">
          Três histórias reais.<br />O fluxo completo em minutos.
        </h1>
        <p className="text-slate-600 mt-5 max-w-2xl leading-relaxed">
          Como candidatos e empresas usam o First2Job no dia-a-dia. Cada história mostra o problema, os
          passos concretos na plataforma e o resultado — para veres exatamente onde clicar e o que esperar.
        </p>
      </section>

      {STORIES.map((story, i) => <Story key={story.id} story={story} index={i} />)}

      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-16 border-t border-black/5">
        <div className="rounded-3xl bg-slate-950 text-white p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-amber-500/20 blur-3xl" aria-hidden="true" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-400 mb-3">A tua vez</p>
              <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl leading-tight">
                Regista-te agora e experimenta o fluxo completo.
              </h2>
              <p className="text-slate-400 mt-4 max-w-md leading-relaxed">
                Grátis para candidatos, para sempre. Publicar uma vaga também é grátis — pagas só se
                quiseres destaque.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
              <Button data-testid="cases-cta-candidate" asChild
                className="rounded-full bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold">
                <Link to="/auth?tab=register">Sou candidato <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              </Button>
              <Button data-testid="cases-cta-company" asChild variant="outline"
                className="rounded-full border-white/20 text-white bg-transparent hover:bg-white/10 hover:text-white">
                <Link to="/auth?tab=register">Sou empresa <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
